import { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../components/Navbar";
import PracticeCard from "../components/PracticeCard";
import api, { LANGUAGES } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import { Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const CARD_COUNT = 6;
const TOPICS = ["food & cooking", "travel", "family", "work & career", "weather & seasons",
  "hobbies", "emotions & feelings", "shopping", "health & body", "technology",
  "daily routine", "nature & animals", "sports", "music & arts", "school & study",
  "home & furniture", "friendship", "time & dates", "directions & places", "money"];
const levelLabel = (pts) => (pts < 100 ? "Level 1 · Words" : pts < 500 ? "Level 2 · Phrases" : "Level 3 · Conversation");
const shuffle = (arr) => [...arr].sort(() => Math.random() - 0.5);
const pickTopic = () => TOPICS[Math.floor(Math.random() * TOPICS.length)];

export default function Flashcards() {
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const [lang, setLang] = useState("Spanish");
  const [cards, setCards] = useState([]);      // [{card, step, refreshing, flash}]
  const [loading, setLoading] = useState(true);
  const [deductLeft, setDeductLeft] = useState(3);   // max 3 deductions per deck
  const seenRef = useRef(new Set());           // fronts seen this session — never repeat until reset
  const langRef = useRef(lang);
  const cardsRef = useRef([]);                  // live mirror of cards for stable reads
  const refreshCounts = useRef({});             // per-slot manual refresh counter
  const points = user?.points || 0;

  const setFlash = (i, text) => {
    setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, flash: text } : c)));
    setTimeout(() => setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, flash: null } : c))), 900);
  };

  useEffect(() => { langRef.current = lang; }, [lang]);
  useEffect(() => { cardsRef.current = cards; }, [cards]);

  // generate ONE unique card, avoiding everything seen this session (all callbacks are stable)
  const genUnique = useCallback(async (step, seed) => {
    const { data } = await api.post("/flashcards/practice", {
      language: langRef.current, step, seed, avoid: Array.from(seenRef.current),
    });
    if (data.card?.front) seenRef.current.add(data.card.front.toLowerCase().trim());
    return data.card;
  }, []);

  const buildDeck = useCallback(async () => {
    setLoading(true);
    seenRef.current = new Set();               // reset the deck
    refreshCounts.current = {};
    setDeductLeft(3);
    const topics = shuffle(TOPICS).slice(0, CARD_COUNT);
    try {
      const built = [];
      for (let i = 0; i < CARD_COUNT; i++) {
        // eslint-disable-next-line no-await-in-loop
        const card = await genUnique(0, topics[i]);  // sequential -> each sees growing 'seen'
        built.push({ card, step: 0, refreshing: false });
      }
      setCards(built);
    } catch (e) {
      console.error("build deck failed", e);
      toast.error("Could not load practice cards.");
    } finally {
      setLoading(false);
    }
  }, [genUnique]);

  // build ONLY on mount and when the language actually changes (never as a passive re-fire)
  const didInit = useRef(false);
  const lastLang = useRef(lang);
  useEffect(() => {
    if (!didInit.current) { didInit.current = true; lastLang.current = lang; buildDeck(); return; }
    if (lastLang.current !== lang) { lastLang.current = lang; buildDeck(); }
  }, [lang, buildDeck]);

  // refresh ONE slot only: mark old card seen, generate a new harder unique card
  const refreshCard = async (i) => {
    const slot = cardsRef.current[i];
    if (!slot || slot.refreshing) return;
    const old = slot.card?.front;
    if (old) seenRef.current.add(old.toLowerCase().trim());
    const nextStep = (slot.step || 0) + 1;     // progressive difficulty per slot
    refreshCounts.current[i] = (refreshCounts.current[i] || 0) + 1;
    if (refreshCounts.current[i] > 3) {
      toast("Struggling with this one? Try dropping to an easier level in Challenge Mode (Profile).", { icon: "💡" });
    }
    setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, refreshing: true } : c)));
    try {
      const card = await genUnique(nextStep, pickTopic());
      setCards((cs) => cs.map((c, idx) => (idx === i ? { card, step: nextStep, refreshing: false } : c)));
    } catch (e) {
      console.error("refresh card failed", e);
      setCards((cs) => cs.map((c, idx) => (idx === i ? { ...c, refreshing: false } : c)));
      toast.error("Could not refresh the card.");
    }
  };

  // self-study scoring
  const markCorrect = async (i) => {
    try {
      const { data } = await api.post("/flashcards/correct");
      await refreshUser();
      refreshCounts.current[i] = 0;
      if (data.awarded > 0) {
        setFlash(i, `+${data.awarded}`);
        if (data.new_badges?.length) data.new_badges.forEach((b) => toast.success(`🏅 Achievement unlocked: ${b.label}!`));
        else if (data.leveled_up) toast.success(`Level up → ${data.tier}!`);
        else if (data.capped) toast("That's your daily solo bonus — live conversations give unlimited points! 💬");
        setTimeout(() => refreshCard(i), 750);   // serve the next card after the animation
      } else {
        toast("Daily solo-practice bonus maxed out — earn unlimited points in a live conversation! 💬");
        setTimeout(() => refreshCard(i), 400);
      }
    } catch (e) {
      console.error("mark correct failed", e);
      toast.error("Could not save your points.");
    }
  };

  const markIncorrect = async (i) => {
    if (deductLeft <= 0) return;
    try {
      const { data } = await api.post("/flashcards/incorrect");
      await refreshUser();
      setFlash(i, `-${data.deducted}`);
      setDeductLeft((v) => Math.max(0, v - 1));
    } catch (e) {
      console.error("mark incorrect failed", e);
      toast.error("Could not update your points.");
    }
  };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading font-black tracking-tight">{t("flash.title")}</h1>
            <p className="text-foreground/70 mt-1">{t("flash.sub")}</p>
          </div>
          <button onClick={buildDeck} disabled={loading} data-testid="flashcard-reset-deck"
            className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary/40 transition-colors disabled:opacity-60">
            <RotateCcw className="w-4 h-4" /> Reset deck
          </button>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/15 text-secondary px-4 py-1.5 text-sm font-semibold" data-testid="flashcard-level-indicator">
            <Sparkles className="w-4 h-4" /> {points} pts · {levelLabel(points)}
          </div>
          <div className="inline-flex items-center gap-2 rounded-full bg-destructive/10 text-destructive px-4 py-1.5 text-sm font-semibold" data-testid="flashcard-deductions-left">
            {deductLeft} deductions left
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Your starting level follows your total points — pick up right where your last call left off. Mark each card <span className="font-semibold text-secondary">Correct</span> (+10) or <span className="font-semibold text-destructive">Incorrect</span> (−5), or hit <span className="font-semibold">Refresh</span> to go a step harder. Seen cards won't repeat until you reset the deck.</p>

        <div className="flex flex-wrap gap-2 mt-6" data-testid="flashcard-lang-picker">
          {LANGUAGES.map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`flashcard-lang-${l.toLowerCase()}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${lang === l ? "bg-primary text-primary-foreground border-primary" : "border-border text-foreground/70 hover:border-primary/40"}`}>
              {l}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8" data-testid="flashcard-grid">
          {(loading ? Array.from({ length: CARD_COUNT }, () => ({ card: null })) : cards).map((c, i) => (
            <PracticeCard key={`${lang}-${i}-${c.card?.id || "loading"}`} card={c.card} index={i} onRefresh={() => refreshCard(i)} refreshing={c.refreshing}
              onCorrect={() => markCorrect(i)} onIncorrect={() => markIncorrect(i)} incorrectDisabled={deductLeft <= 0} flash={c.flash} />
          ))}
        </div>
      </div>
    </div>
  );
}
